import { Component, OnInit, AfterViewChecked, QueryList, ViewChildren, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseQuestionComponent } from '../base-question.component';
import { QuestionOption } from '@formrails/form-builder/data-access';

/**
 * Radio question component.
 * Displays and configures a radio button list question.
 * Uses Angular signals inherited from BaseQuestionComponent.
 */
@Component({
  selector: 'app-radio-question',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './radio-question.component.html',
  styleUrl: './radio-question.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RadioQuestionComponent extends BaseQuestionComponent implements OnInit, AfterViewChecked {
  @ViewChildren('optionInput') optionInputs!: QueryList<ElementRef<HTMLInputElement>>;

  options: QuestionOption[] = [];
  selectedOptionIndex: number | null = null;
  private newOptionIndex: number | null = null;

  ngOnInit(): void {
    const q = this.question();
    // Initialize options from question model or start with one empty option
    if (q.options && q.options.length > 0) {
      // Convert string[] to QuestionOption[] if needed (backward compatibility)
      if (typeof q.options[0] === 'string') {
        this.options = (q.options as string[]).map(text => ({
          text,
          isCustomOption: false
        }));
      } else {
        this.options = (q.options as QuestionOption[]).map(opt => ({
          text: opt.text,
          isCustomOption: opt.isCustomOption || false,
          customValue: opt.customValue
        }));
      }
    } else {
      this.options = [{ text: '', isCustomOption: false }];
    }

    // Initialize custom option values from question model
    // Create a new object if customOptionValues doesn't exist (question may be frozen)
    if (!q.customOptionValues) {
      this.emitQuestionChange({ ...q, customOptionValues: {} });
    }
  }

  onLabelChange(value: string): void {
    this.updateLabel(value);
  }

  onHelperTextChange(value: string): void {
    this.updateHelperText(value);
  }

  onAdditionalInfoChange(value: string): void {
    this.updateAdditionalInfo(value);
  }

  onOptionChange(index: number, value: string): void {
    // Update local array immediately for responsive UI
    this.options[index] = { ...this.options[index], text: value };
    // Persist on every keystroke so newly added options are registered without requiring a blur
    this.saveOptions();
  }

  onOptionBlur(): void {
    // Save options when user leaves the input field
    this.saveOptions();
  }

  onCustomOptionToggle(index: number, enabled: boolean): void {
    this.options[index] = { ...this.options[index], isCustomOption: enabled };
    this.saveOptions();
  }

  onCustomValueChange(index: number, value: string): void {
    this.options[index] = { ...this.options[index], customValue: value };
    const q = this.question();
    // Create a new customOptionValues object (question may be frozen)
    const currentCustomValues = q.customOptionValues || {};
    const updatedCustomValues = { ...currentCustomValues, [index]: value };
    this.emitQuestionChange({ ...q, customOptionValues: updatedCustomValues });
  }

  onRadioSelect(index: number): void {
    this.selectedOptionIndex = index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  addOption(): void {
    const newIndex = this.options.length;
    this.options.push({ text: '', isCustomOption: false });
    this.newOptionIndex = newIndex;
    this.saveOptions();
  }

  ngAfterViewChecked(): void {
    if (this.newOptionIndex !== null && this.optionInputs && this.optionInputs.length > this.newOptionIndex) {
      const inputElement = this.optionInputs.toArray()[this.newOptionIndex]?.nativeElement;
      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
          this.newOptionIndex = null;
        }, 0);
      }
    }
  }

  removeOption(index: number): void {
    if (this.options.length > 1) {
      this.options.splice(index, 1);
      const q = this.question();
      // Update custom option values indices
      // Create a new object (question may be frozen)
      if (q.customOptionValues) {
        const newCustomValues: Record<number, string> = {};
        Object.keys(q.customOptionValues).forEach(key => {
          const keyNum = parseInt(key, 10);
          if (keyNum < index) {
            newCustomValues[keyNum] = q.customOptionValues![keyNum];
          } else if (keyNum > index) {
            newCustomValues[keyNum - 1] = q.customOptionValues![keyNum];
          }
        });
        this.emitQuestionChange({ ...q, customOptionValues: newCustomValues });
      }
      this.saveOptions();
    }
  }

  getOptionsForDisplay(): (string | QuestionOption)[] {
    const q = this.question();
    if (!q.options || q.options.length === 0) {
      return [];
    }
    return q.options;
  }

  getOptionText(option: string | QuestionOption): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.text;
  }

  private saveOptions(): void {
    // Preserve the full options list (including empty rows) so newly-added or
    // partially-typed entries survive between edits. Empty entries are only
    // pruned at final-form-submit time by the consumer.
    const optionsToSave = this.options.length > 0
      ? [...this.options]
      : [{ text: '', isCustomOption: false }];
    this.emitConfigChange({ options: optionsToSave } as any);
    this.emitQuestionChange({ ...this.question(), options: optionsToSave });
  }

  onRequiredToggle(): void {
    this.updateRequired(!this.question().isRequired);
  }
}

